import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { COMPANY_LICENCE_COSTS } from '@pk/shared';
import type { Id } from '@pk/shared';
import { actorOf, useCities, useCompanies, useDeliveries, useHouse, useLoans, useWorld } from '../api/hooks';
import { Btn, ErrorBox, PageHeader, Screen, ScreenTop, ScrollArea, Stat } from '../components/ui';
import { ResGold, ResourceIcon } from '../components/icons';
import { CityList, CitySheet } from './trade/MarketsView';
import { CaravansView } from './trade/CaravansView';
import { CompaniesView } from './trade/CompaniesView';
import { LoansView } from './trade/LoansView';
import { GuildsView } from './trade/GuildsView';
import { Skeleton } from './trade/shared';
import '../styles/trade.css';

const VIEWS = ['markets', 'guilds', 'caravans', 'companies', 'loans'] as const;
type View = (typeof VIEWS)[number];

export function TradeScreen() {
  const { t } = useTranslation();
  const cities = useCities();
  const house = useHouse();
  const world = useWorld();
  const deliveries = useDeliveries();
  const companies = useCompanies();
  const loans = useLoans();
  const [params, setParams] = useSearchParams();
  const navRef = useRef<HTMLElement>(null);
  const activeView = params.get('view');
  const ready = !cities.isLoading && !house.isLoading;
  useEffect(() => {
    const box = navRef.current;
    const on = box?.querySelector<HTMLElement>('button.on');
    if (box && on && box.scrollWidth > box.clientWidth) box.scrollTo({ left: on.offsetLeft - (box.clientWidth - on.offsetWidth) / 2 });
  }, [activeView, ready]);

  if (cities.isLoading || house.isLoading) {
    return (<Screen><ScreenTop><PageHeader icon={<ResGold />} title={t('trade.title')} /></ScreenTop><ScrollArea><Skeleton rows={4} /></ScrollArea></Screen>);
  }
  if (cities.isError || !cities.data) return <ErrorBox error={cities.error} onRetry={() => cities.refetch()} />;
  if (!house.data) return <ErrorBox error={house.error} onRetry={() => house.refetch()} />;

  const h = house.data;
  const actor = actorOf(h);
  const gold = Math.floor(h.house.resources.gold);
  const members = h.members.map((m) => ({ id: m.id, name: m.firstName, adult: m.alive && m.age >= 16 }));

  const rawView = params.get('view');
  const view: View = VIEWS.includes(rawView as View) ? (rawView as View) : 'markets';
  const setView = (v: View) => {
    const next = new URLSearchParams(params);
    if (v === 'markets') next.delete('view'); else next.set('view', v);
    next.delete('city'); // a selected city only means something in the markets and guilds views
    next.delete('focus');
    setParams(next, { replace: true });
  };
  const rawCity = params.get('city');
  const city = (view === 'markets' || view === 'guilds') && rawCity ? cities.data.cities.find((c) => c.cityId === rawCity) ?? null : null;
  const setCity = (id: Id | null, focus?: 'guild') => {
    const next = new URLSearchParams(params);
    if (id) next.set('city', id); else next.delete('city');
    if (id && focus) next.set('focus', focus); else next.delete('focus');
    if (view === 'markets') next.delete('view'); // default view is never in the URL
    setParams(next, { replace: true });
  };

  const owned = new Set(members.map((m) => m.id));
  const myCompanies = companies.data?.companies.filter((c) => owned.has(c.ownerId)).length;
  const caravanRows = deliveries.data?.deliveries;
  const loanRows = loans.data?.loans;
  const month = Math.round(h.monthlyBalance.gold);

  // One next step, most pressing first.
  const day = world.data?.day;
  const overdue = loanRows?.find((l) => l.role === 'borrower' && (l.missed > 0 || l.defaulted));
  const nextArrival = caravanRows && caravanRows.length > 0 && day != null ? Math.max(0, Math.min(...caravanRows.map((d) => d.arriveDay)) - day) : null;
  let hint: { text: string; btn: string; go: View } | null = null;
  if (overdue) hint = { text: t('eco.hint.overdue', { name: overdue.lenderName }), btn: t('eco.hint.repay'), go: 'loans' };
  else if (companies.data && myCompanies === 0 && gold >= COMPANY_LICENCE_COSTS[1]) hint = { text: t('eco.hint.found', { gold: COMPANY_LICENCE_COSTS[1] }), btn: t('trade.companies.found'), go: 'companies' };
  else if (caravanRows && caravanRows.length > 0 && nextArrival != null) hint = { text: t('eco.hint.caravan', { count: caravanRows.length, days: nextArrival }), btn: t('eco.hint.seeCaravans'), go: 'caravans' };
  else hint = { text: t('eco.hint.browseText'), btn: t('eco.hint.browse'), go: 'markets' };

  const counts: Record<View, number | undefined> = {
    markets: cities.data.cities.length, guilds: cities.data.cities.filter((c) => (c.myGuildXp ?? 0) > 0).length, caravans: caravanRows?.length, companies: myCompanies, loans: loanRows?.length,
  };

  return (
    <Screen className="trade">
      <ScreenTop>
      <PageHeader icon={<ResGold />} title={t('trade.title')} subtitle={t('eco.subtitle')} />

      <nav className="tr-nav" ref={navRef} role="tablist" aria-label={t('trade.title')}>
        {VIEWS.map((v) => (
          <button key={v} type="button" role="tab" aria-selected={view === v} className={view === v ? 'on' : ''} onClick={() => setView(v)}>
            <span className="tr-nav-label">{t(`trade.view.${v}`)}</span>
            <span className="tr-nav-count num">{counts[v] ?? '–'}</span>
          </button>
        ))}
      </nav>
      </ScreenTop>

      <ScrollArea>
      <div className="tr-stats">
        <Stat icon={<ResourceIcon k="gold" />} label={t('eco.stat.gold')} value={gold} />
        <Stat icon={<ResourceIcon k="gold" />} label={t('eco.stat.month')} value={`${month > 0 ? '+' : ''}${month}`} trend={month > 0 ? 'pos' : month < 0 ? 'neg' : undefined} />
        <Stat icon={<ResourceIcon k="horses" />} label={t('eco.stat.caravans')} value={caravanRows?.length ?? '–'} />
        <Stat icon={<ResourceIcon k="stone" />} label={t('eco.stat.companies')} value={myCompanies ?? '–'} />
      </div>

      {hint && (
        <div className={`tr-hint ${overdue ? 'warn' : ''}`}>
          <p>{hint.text}</p>
          <Btn small variant={overdue ? 'primary' : 'ghost'} onClick={() => setView(hint.go)}>{hint.btn}</Btn>
        </div>
      )}

      {view === 'markets' && <CityList cities={cities.data.cities} onPick={(id) => setCity(id)} />}
      {view === 'guilds' && <GuildsView cities={cities.data.cities} onOpen={(id) => setCity(id, 'guild')} />}
      {view === 'caravans' && <CaravansView actor={actor} gold={gold} onBrowse={() => setView('markets')} />}
      {view === 'companies' && <CompaniesView actor={actor} gold={gold} members={members} />}
      {view === 'loans' && <LoansView actor={actor} gold={gold} />}
      </ScrollArea>

      {city && <CitySheet cityId={city.cityId} name={city.name} actor={actor} houseId={h.house.id} gold={gold} onClose={() => setCity(null)} focusGuild={params.get('focus') === 'guild'} />}
    </Screen>
  );
}
